import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { findSubmodule } from '../data/modules';
import EndpointCard from '../components/EndpointCard';
import ApiConfigModal from '../components/ApiConfigModal';
import { useApiConfig } from '../context/ApiConfigContext';

// Cache fetched + parsed docs so we don't re-fetch on navigation
const docsCache = {};

function methodColor(method) {
  const map = {
    GET: 'var(--get)',
    POST: 'var(--post)',
    PUT: 'var(--put)',
    DELETE: 'var(--delete)',
    PATCH: 'var(--patch)',
  };
  return map[method] || 'var(--text)';
}

export default function ModulePage({ theme, onToggleTheme }) {
  const { moduleId, submoduleId } = useParams();
  const navigate = useNavigate();
  const { baseUrl } = useApiConfig();

  const info = findSubmodule(moduleId, submoduleId);

  const [moduleData, setModuleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [configOpen, setConfigOpen] = useState(false);

  // Reset search on navigation
  useEffect(() => {
    setSearchQuery('');
  }, [moduleId, submoduleId]);

  // Redirect to first submodule if moduleId/submoduleId are invalid
  useEffect(() => {
    if (!info) {
      navigate('/crm/leads', { replace: true });
    }
  }, [info, navigate]);

  // Fetch JSON doc file
  useEffect(() => {
    if (!info) return;

    const { submodule } = info;
    const cacheKey = submodule.file;

    if (docsCache[cacheKey]) {
      setModuleData(docsCache[cacheKey]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetch(`${process.env.PUBLIC_URL}/docs/${submodule.file}`)
      .then(res => {
        if (!res.ok) throw new Error(`Failed to load ${submodule.file}`);
        return res.json();
      })
      .then(data => {
        docsCache[cacheKey] = data;
        setModuleData(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [info]);

  // Filter endpoints by search query
  const filteredEndpoints = useMemo(() => {
    if (!moduleData) return [];
    const q = searchQuery.toLowerCase().trim();
    if (!q) return moduleData.endpoints;
    return moduleData.endpoints.filter(ep =>
      ep.path.toLowerCase().includes(q) ||
      ep.method.toLowerCase().includes(q) ||
      ep.desc.toLowerCase().includes(q)
    );
  }, [moduleData, searchQuery]);

  // Count endpoints by method
  const stats = useMemo(() => {
    if (!moduleData) return {};
    return moduleData.endpoints.reduce((acc, ep) => {
      acc[ep.method] = (acc[ep.method] || 0) + 1;
      return acc;
    }, {});
  }, [moduleData]);

  if (!info) return null;
  const { module: mod, submodule: sub } = info;

  return (
    <>
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-left">
          <div className="breadcrumb">
            <span>{mod.name}</span>
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-active">{sub.name}</span>
          </div>
        </div>
        <div className="topbar-right">
          {moduleData && moduleData.basePath && (
            <div className="base-url-pill">
              <span className="base-url-dot" />
              {moduleData.basePath}
            </div>
          )}
          {/* API config / user button */}
          <button
            className={`topbar-user-btn${baseUrl ? ' configured' : ''}`}
            onClick={() => setConfigOpen(true)}
            title={baseUrl ? `Configured: ${baseUrl}` : 'Configure API'}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
          </button>
          <button className="topbar-theme-btn" onClick={onToggleTheme} title="Toggle theme">
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
        </div>
      </div>

      {/* API Config Modal */}
      {configOpen && <ApiConfigModal onClose={() => setConfigOpen(false)} />}

      {/* Content */}
      <div className="content">
        {loading && (
          <div className="loading-wrap">
            <div className="spinner" />
            <span>Loading {sub.name}…</span>
          </div>
        )}

        {error && (
          <div className="loading-wrap">
            <span style={{ color: 'var(--delete)' }}>⚠ {error}</span>
          </div>
        )}

        {!loading && !error && moduleData && (
          <>
            {/* Module header */}
            <div className="module-header">
              <div className="module-tag">{mod.name}</div>
              <div className="module-title">
                {sub.icon} {sub.name}
              </div>
              {moduleData.description && (
                <div className="module-desc">{moduleData.description}</div>
              )}

              {/* Stats row */}
              {Object.keys(stats).length > 0 && (
                <div className="stats-row">
                  {Object.entries(stats).map(([method, count]) => (
                    <div key={method} className="stat-pill">
                      <div>
                        <div className="stat-num" style={{ color: methodColor(method) }}>
                          {count}
                        </div>
                        <div className="stat-label">{method}</div>
                      </div>
                    </div>
                  ))}
                  <div className="stat-pill">
                    <div>
                      <div className="stat-num">{moduleData.endpoints.length}</div>
                      <div className="stat-label">Total</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Search */}
            <div className="search-wrap">
              <input
                className="search-input"
                type="text"
                placeholder={`Search ${sub.name} endpoints…`}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Endpoint cards */}
            {filteredEndpoints.length === 0 ? (
              <div className="no-results">
                {searchQuery
                  ? `No endpoints match "${searchQuery}"`
                  : 'No endpoints found in this module.'}
              </div>
            ) : (
              filteredEndpoints.map((ep, i) => (
                <EndpointCard key={`${ep.method}-${ep.path}-${i}`} endpoint={ep} />
              ))
            )}
          </>
        )}
      </div>
    </>
  );
}
